import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { checkoutStatus, API } from "../lib/api";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";

const MAX_POLLS = 8;

export default function CheckoutSuccess() {
  const [search] = useSearchParams();
  const sessionId = search.get("session_id");
  const orderId = search.get("order_id") || search.get("token");  // PayPal returns ?token=ORDER_ID
  const subId = search.get("subscription_id");
  const processor = search.get("processor") || (orderId || subId ? "paypal" : "stripe");
  const kind = search.get("kind") || "boost";
  const [state, setState] = useState({ phase: "polling", data: null, error: "" });

  useEffect(() => {
    // ── PayPal one-time (Boost) flow: capture the order, then we're done ──
    if (processor === "paypal" && orderId) {
      let cancelled = false;
      (async () => {
        try {
          const r = await axios.get(`${API}/checkout/paypal/capture`, { params: { order_id: orderId } });
          if (cancelled) return;
          if (r.data?.paid) {
            setState({ phase: "ok", data: { kind: "boost", processor: "paypal" }, error: "" });
          } else {
            setState({ phase: "timeout", data: r.data, error: "" });
          }
        } catch (e) {
          if (!cancelled) setState({ phase: "error", data: null, error: e?.response?.data?.detail || "Could not confirm PayPal payment." });
        }
      })();
      return () => { cancelled = true; };
    }
    // ── PayPal subscription: webhook flips the tx; we just show "confirming" ──
    if (processor === "paypal" && subId) {
      setState({ phase: "ok", data: { kind: "subscription", processor: "paypal" }, error: "" });
      return;
    }
    // ── Stripe flow (existing) ──
    if (!sessionId) {
      setState({ phase: "error", data: null, error: "No session id." });
      return;
    }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      if (cancelled) return;
      tries += 1;
      try {
        const r = await checkoutStatus(sessionId);
        if (cancelled) return;
        if (r.payment_status === "paid") {
          setState({ phase: "ok", data: r, error: "" });
          return;
        }
        if (r.status === "expired" || tries >= MAX_POLLS) {
          setState({ phase: r.status === "expired" ? "expired" : "timeout", data: r, error: "" });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ phase: "error", data: null, error: e?.response?.data?.detail || "Could not confirm payment." });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, orderId, subId, processor]);

  const title = state.phase === "ok"
    ? (kind === "sub" || state.data?.kind === "subscription"
        ? `Welcome to Newseze ${state.data?.tier === "pro" ? "Pro" : state.data?.tier === "plus" ? "Plus" : "Subscriber"}.`
        : `Subject boosted${state.data?.subject ? ": " + state.data.subject : ""}`)
    : state.phase === "polling" ? "Confirming your payment…"
    : state.phase === "timeout" ? "Still confirming — check back shortly."
    : state.phase === "expired" ? "Checkout expired."
    : "Something went wrong.";

  return (
    <>
      <Header onOpenBrief={() => {}} />
      <main className="max-w-[720px] mx-auto px-5 py-16 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--sky)] mb-5">
          {state.phase === "polling" ? <Loader2 className="text-[var(--blue)] animate-spin" size={28} />
            : state.phase === "ok" ? <CheckCircle2 className="text-[var(--blue)]" size={32} />
            : <AlertCircle className="text-[var(--red,#b91c1c)]" size={28} />}
        </div>
        <h1 className="text-[32px] font-black tracking-[-0.02em]" data-testid="checkout-success-title">{title}</h1>
        {state.phase === "ok" && (
          <p className="text-[15px] text-[var(--muted)] mt-3 max-w-[520px] mx-auto">
            {state.data?.kind === "boost"
              ? "Your subject is now prioritized in your Buildeze feed for 30 days and earns 2× credits."
              : "Your subscription is active. Premium reward tiers are now unlocked."}
          </p>
        )}
        {state.error && <p className="text-[14px] text-[var(--red,#b91c1c)] mt-3" data-testid="checkout-success-error">{state.error}</p>}
        <div className="mt-7 flex gap-3 justify-center">
          <Link to="/" className="btn primary" data-testid="checkout-success-home">Back to Newseze</Link>
        </div>
      </main>
      <Footer />
    </>
  );
}
